<script setup lang="tsx">
import { ref, onBeforeMount, watch } from 'vue'
import type { ImportProgress, JiraDomainConfigResponse, JiraResponse, Response } from '@/models/api-generated'
import {
	ImportInProgress,
	TaskUnsuccessful,
	RefreshNeeded,
	NoDomainConfigForInstance,
	StartSync,
	StartImport,
} from '@/models/jira-enums-generated'
import jiraCbStore from '@/stores/jira-checkboxes'
import { storeToRefs } from 'pinia'
import methodEditor from '@/stores/method-editor'
import http from '@/services/http'

const jiraCb = jiraCbStore()
const { isSomethingWrong, jiraStatus, action } = storeToRefs(jiraCb)
const editor = methodEditor()
const progress = ref<ImportProgress[]>([])

async function initializeProjectAndImportIssues() {
	jiraStatus.value = ImportInProgress
	let startingTime = new Date().toISOString()
	fetchProgress(startingTime)
	let checkEverything = false
	if (action.value === StartSync) {
		checkEverything = true
	}
	const { data } = await http.get<Response<JiraResponse>>(`/api/jira/initializeAndImport?checkEverything=${checkEverything}`)
	if (data.data) jiraStatus.value = data.data.status
	else {
		jiraStatus.value = TaskUnsuccessful
		isSomethingWrong.value = true
		return
	}

	if (jiraStatus.value === RefreshNeeded) await editor.refresh()
	if (jiraStatus.value === NoDomainConfigForInstance) isSomethingWrong.value = true
	action.value = undefined
}

async function fetchProgress(startingTime: string) {
	const { data } = await http.get<Response<ImportProgress[]>>(`/api/jira/importProgress?startingTime=${encodeURIComponent(startingTime)}`)
	if (data.data) progress.value = data.data
	else {
		jiraStatus.value = TaskUnsuccessful
		isSomethingWrong.value = true
		return
	}

	if (!progress.value.length || jiraStatus.value !== ImportInProgress) {
		progress.value = []
		action.value = undefined
		return
	} else {
		setTimeout(() => fetchProgress(startingTime), 500)
	}
}

async function checkAutomaticImport() {
	const { data } = await http.get<Response<JiraDomainConfigResponse>>('api/jira/domainConfigs')
	if (!data.data) return
	const domainConfigs = data.data.configs
	if (!domainConfigs || domainConfigs.some((config) => config.automaticImport === false)) {
		return
	}
	initializeProjectAndImportIssues()
}

watch(action, (newAction) => {
	if (newAction === StartSync || newAction === StartImport) {
		initializeProjectAndImportIssues()
	}
})

onBeforeMount(checkAutomaticImport)
</script>

<template>
	<div v-if="progress.length || action === StartSync" class="d-flex justify-content-center my-3">
		<div class="alert alert-info text-center shadow-sm p-3" role="alert">
			<h6>{{ $t('jira.actionProgress') }}</h6>
			<div v-for="(item, index) in progress" :key="index">
				<p class="mb-3">
					{{
						item.standardUpgradeIssueCount > 0
							? $t('jira.standardUpgradeIssues', { instance: item.domainConfig?.instance?.name, count: item.standardUpgradeIssueCount })
							: $t('jira.issuesToImport', { instance: item.domainConfig?.instance?.name, count: item.issueCount })
					}}
				</p>
			</div>
		</div>
	</div>
</template>
